import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { fetchDeployment, fetchTrajectories } from '../../services/api';
import { Deployment } from '../../types/types';
import { extractDeploymentNumber } from '../../utils/deployment';
import { findTrajectoryByColor } from '../../utils/trajectoryColor';

interface TrajectoryMapProps {
  selectedDeployment: string;
}

const TrajectoryMap: React.FC<TrajectoryMapProps> = ({
  selectedDeployment,
}) => {
  const mapRef = useRef<L.Map | null>(null);
  const [gpxLayers, setGpxLayers] = useState<L.Layer[]>([]);
  const [noTrajectory, setNoTrajectory] = useState<boolean>(false);
  const [deployment, setDeployment] = useState<Deployment | null>(null);

  const loadGPXFile = (trajectory: any, map: L.Map) => {
    const polylineOptions = {
      color: trajectory.color,
      opacity: 0.75,
      weight: 5,
      lineCap: 'round',
    };
    const gpxLayer = new (L as any).GPX(trajectory.url, {
      async: true,
      polyline_options: polylineOptions,
      marker_options: {
        startIconUrl: '',
        endIconUrl: '',
        shadowUrl: '',
      },
    });

    gpxLayer.on('loaded', function (e: any) {
      const polyline = e.target.getLayers()[0];
      if (deployment) {
        const trajectoryData = findTrajectoryByColor(
          deployment,
          trajectory.color
        );
        if (trajectoryData) {
          const conditions = trajectoryData.conditions || {};
          const formattedConditions = Object.entries(conditions)
            .map(([key, value]) => {
              const displayName = key.charAt(0).toUpperCase() + key.slice(1);
              const colorClass = value
                ? 'bg-green-200 text-green-800'
                : 'bg-gray-200 text-gray-600';
              return `<span class="px-2 py-0.5 rounded-full text-xs font-medium ${colorClass} mr-1">${displayName}: ${value ? 'Yes' : 'No'}</span>`;
            })
            .join(' ');

          const titleColor =
            trajectoryData.color.toLowerCase() === 'yellow'
              ? 'black'
              : trajectoryData.color;

          polyline.bindPopup(`
                  <div class="bg-white p-3 rounded-md border-[2px] shadow-lg" style="border-color: ${trajectoryData.color}; width: 220px;">
                    <h4 class="text-sm font-semibold mb-2" style="color: ${titleColor};">
                      ${trajectoryData.color} trajectory
                    </h4>
                    <p class="text-xs text-gray-700 mb-1">
                      Length: <span class="font-semibold">${trajectoryData.length} m</span>
                    </p>
                    <div class="text-xs">
                      <strong>Conditions:</strong><br/>
                      ${formattedConditions}
                    </div>
                  </div>
                `);
        }
      }
    });

    gpxLayer.addTo(map);
    return gpxLayer;
  };

  useEffect(() => {
    const deploymentId = extractDeploymentNumber(selectedDeployment);
    if (deploymentId) {
      fetchDeployment(deploymentId)
        .then((data) => {
          setDeployment(data);
        })
        .catch((err) => {
          console.error('Error fetching deployment data:', err);
        });
    }
  }, [selectedDeployment]);

  useEffect(() => {
    if (!deployment) return;

    const mapContainer = document.getElementById('map-trajectory');
    if (!mapContainer || mapContainer.innerHTML !== '') return;

    const gpxScript = document.createElement('script');
    gpxScript.src =
      'https://cdnjs.cloudflare.com/ajax/libs/leaflet-gpx/1.7.0/gpx.min.js';
    gpxScript.async = true;

    gpxScript.onload = async () => {
      if (!mapRef.current) {
        mapRef.current = L.map('map-trajectory').setView(
          [47.322617, -71.145015],
          14
        );
        L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          attribution: '&copy; OpenStreetMap contributors',
        }).addTo(mapRef.current);
      }

      const trajectories = await fetchTrajectories(selectedDeployment);

      if (trajectories.length === 0) {
        setNoTrajectory(true);
      } else {
        setNoTrajectory(false);
      }

      gpxLayers.forEach((layer) => {
        mapRef.current?.removeLayer(layer);
      });

      const newLayers = trajectories.map((trajectory: any) =>
        loadGPXFile(trajectory, mapRef.current!)
      );
      setGpxLayers(newLayers);
    };

    document.body.appendChild(gpxScript);

    return () => {
      document.body.removeChild(gpxScript);
      gpxLayers.forEach((layer) => {
        mapRef.current?.removeLayer(layer);
      });
    };
  }, [deployment]);

  if (!deployment) {
    return <div>Loading deployment data...</div>;
  }

  if (noTrajectory) {
    return <div>No trajectory to display for this deployment</div>;
  }

  return (
    <div id="map-trajectory" className="h-[400px] w-full rounded shadow-md" />
  );
};

export default TrajectoryMap;
